import {
    Button,
    Label,
    List,
    Pressable,
    Text,
    TextInput,
    ThemeComponentSwitcher,
    View,
    fromTheme,
    nativeOnlyStyles,
    styled,
    webOnlyStyles
} from 'lib_ui-primitives';

export const ScanLabelAndField = styled(View).attrs({ name: 'scan-label-and-field' })`
    flex-direction: row;
`;
ScanLabelAndField.displayName = 'ScanLabelAndField';

const RowDetailStyleOld = styled(Text).attrs({ name: 'row-detail' })`
    line-height: ${fromTheme('listLineHeight')};
    text-align: center;
    padding-left: ${fromTheme('textPadding')};
    white-space: nowrap;
    overflow: hidden;
    ${({ displayStyle, theme }) => {
        if (displayStyle === 'indented') {
            return `padding-left: ${theme.textPadding * 3}px;`;
        }
        if (displayStyle === 'bold') {
            return 'font-weight: bold;';
        }
        if (displayStyle === 'italic') {
            return 'font-style: italic;';
        }
    }}
    color: ${({ disabled, theme }) => {
        return disabled ? theme.disabledFontColor : theme.defaultFontColor;
    }};
`;
RowDetailStyleOld.displayName = 'RowDetailStyle';

const RowDetailStyleGl2025 = styled(Text).attrs({ name: 'row-detail' })`
    color: ${({ disabled }) => fromTheme('dropdown', 'row', 'text', 'color', disabled ? 'disabled' : 'default')};
`;
RowDetailStyleGl2025.displayName = 'RowDetailStyle';

export const RowDetailStyle = ThemeComponentSwitcher([
    { theme: 'gl2025', component: RowDetailStyleGl2025 },
    { theme: 'old', component: RowDetailStyleOld }
]);

/*
Make dropdown menu at least as tall as one record to so the user will
see an empty dropdown (as opposed to nothing) when there are no records.
*/
const _Menu = styled(View).attrs({ name: 'Menu' })`
    z-index: ${({ theme }) => theme.zindex.DropDownList};
    height: ${props => {
        if (!props.visible) return 0;
        if (props.theme.mobile) return props.theme.width - 60 + 'px';
        return props.itemCount < 5 ? Math.max(props.itemCount, 1) * props.theme.listLineHeight + 'px' : '200px';
    }};
    max-width: ${props => (props?.theme.mobile ? '100%' : props.theme.form.entryMinWidth + 'px')};
    width: ${props => {
        const {
            visible,
            theme: { mobile, width }
        } = props;
        return mobile && visible ? width - 12 + 'px' : '100%';
    }};
    flex-grow: ${props => (!props?.visible ? 0 : 1)};
    margin-top: ${props => (props.visible && props.theme.mobile ? '6px' : '0')};
    ${({ theme }) =>
        theme.mobile &&
        `
            margin-bottom: ${theme.viewMargin}px;
            margin-left: ${theme.viewMargin}px;
        `}
`;
const MenuOld = webOnlyStyles(_Menu)`
    background-color: ${props => (props.theme.darkMode ? 'black' : 'white')};
    box-shadow: ${props => {
        if (props.visible && props.theme.mobile) {
            return 'unset';
        }
        if (props.theme.darkMode) {
            return '8px 11px 13px -2px #000000f0, 4px 23px 39px 0px #5787a9c0';
        } else {
            return '4px 4px 11px -2px #000000d0, 4px 16px 32px 4px #5787a970';
        }
    }}
`;
MenuOld.displayName = 'Menu';

// dynamic width based on width of corresponding input
// menu height is calculated by adding the height of each row + the menu padding.
// each row's height is calculated by multiplying the number of lines by pixelsPerLineOfText
// then adding paddingPerRow
const _MenuGl2025 = styled(View).attrs({ name: 'Menu' })`
    display: flex;
    flex-direction: column;
    padding: ${fromTheme('dropdown', 'menu', 'padding')};
    border-radius: ${fromTheme('dropdown', 'menu', 'border', 'radius')};
    border: ${fromTheme('dropdown', 'menu', 'border', 'width')} solid
        ${fromTheme('dropdown', 'menu', 'border', 'color')};
    background: ${fromTheme('dropdown', 'menu', 'backgroundColor')};
    width: ${({ theme, width }) => (theme.mobile ? '100%' : `${width - theme.formField.padding.left - theme.formField.padding.right}px`)};
    height: ${({ numLinesArray, theme }) => {
        if (theme.mobile) return '100%';
        const pixelsPerLineOfText = theme.dropdown.row.pixelsPerLineOfText; // 17px
        const paddingPerRow = theme.dropdown.row.padding.vertical * 2; // 16px
        const menuPadding = theme.dropdown.menu.padding * 2 + theme.dropdown.menu.border.width * 2; // 18px
        const maxNumLines = 5;
        const minHeight = pixelsPerLineOfText + paddingPerRow; // 33px
        const maxHeight = maxNumLines * (pixelsPerLineOfText + paddingPerRow) + menuPadding; // 181 px
        return (
            (numLinesArray.reduce((totalLines, numLinesCurRow) => totalLines + numLinesCurRow) < maxNumLines
                ? Math.max(
                      numLinesArray.reduce(
                          (totalHeight, numLinesCurRow) =>
                              totalHeight + (numLinesCurRow * pixelsPerLineOfText + paddingPerRow),
                          menuPadding + 2
                      ),
                      minHeight
                  )
                : getRoundedMaxHeight(numLinesArray, maxHeight, pixelsPerLineOfText, paddingPerRow, menuPadding)) + 'px'
        );
    }};
`;
const MenuGl2025 = webOnlyStyles(_MenuGl2025)`
    gap: 0;
    box-shadow: ${fromTheme('dropdown', 'menu', 'boxShadow')};
`;
MenuGl2025.displayName = 'Menu';

export const Menu = ThemeComponentSwitcher([
    { theme: 'gl2025', component: MenuGl2025 },
    { theme: 'old', component: MenuOld }
]);

const PreDropdownListOld = styled(List)`
    min-height: ${props => {
        const {
            theme: { mobile },
            itemCount
        } = props;
        return mobile ? '200px' : props.itemCount < 5 ? itemCount * 42 + 'px' : '200px';
    }};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: ${fromTheme('borderRadius')};
    border-bottom-right-radius: ${fromTheme('borderRadius')};
    background-color: ${props => (props.theme.mobile ? props.theme.baseBackgroundColorDarker : 'transparent')};
    flex-grow: 1;
    display: ${({ hide }) => (hide ? 'none' : 'flex')};
`;
const DropdownListOld = webOnlyStyles(PreDropdownListOld)`
    height: 100%;
`;
DropdownListOld.displayName = 'DropdownList';

const PreDropdownListGl2025 = styled(List)`
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: ${fromTheme('borderRadius')};
    border-bottom-right-radius: ${fromTheme('borderRadius')};
    flex-grow: 1;
    display: ${({ hide }) => (hide ? 'none' : 'flex')};
`;
const DropdownListGl2025 = webOnlyStyles(PreDropdownListGl2025)`
    height: 100%;
`;
DropdownListGl2025.displayName = 'DropdownList';

export const DropdownList = ThemeComponentSwitcher([
    { theme: 'gl2025', component: DropdownListGl2025 },
    { theme: 'old', component: DropdownListOld }
]);

let _ListItem = styled(Pressable).attrs({ name: 'list-item', block: true, tabIndex: 0 })`
    line-height: ${fromTheme('listLineHeight')};
    text-align: left;
    border-width: 0;
    background-color: ${({ isActive, rowIndex, isSelected, theme }) => {
        if (isActive) {
            return theme.listActiveBackgroundColor;
        }
        if (isSelected) {
            return theme.listSelectedBackgroundColor;
        }
        return rowIndex % 2 === 0 ? theme.backgroundColor : theme.backgroundColorDarker;
    }};
    font-weight: ${({ isSelected }) => (isSelected ? 'bold' : 'normal')};
    right: 0px;
`;

/*
    This is a desperate concession to necessity.  After hours, I just couldn't get the RN width
    to be right without using this calc. :(  Fortunately, all RN dropdownlists will display
    in a modal so I don't need to worry about the calc being wrong due to nesting for
    instance
*/
_ListItem = nativeOnlyStyles(_ListItem)`
    width: ${({ theme }) => theme.width - theme.textPadding * 2 + 'px'};
`;
// Focus outline is handled by the downshift setHighlightIndex method (which sets a style)
_ListItem = webOnlyStyles(_ListItem)`
    &:focus-visible {
        outline-width: 0;
    }
    overflow: hidden;
    cursor: pointer;
`;

const ListItemOld = _ListItem;
ListItemOld.displayName = 'ListItem';

let _ListItemGl2025 = styled(Pressable).attrs({ name: 'list-item', tabIndex: 0 })`
    display: flex;
    padding: ${fromTheme('dropdown', 'row', 'padding', 'vertical')}
        ${fromTheme('dropdown', 'row', 'padding', 'horizontal')};
    align-items: flex-start;
    border-radius: ${({ theme }) => (theme.mobile ? 0 : theme.dropdown.row.borderRadius + 'px')};
    background-color: ${fromTheme('dropdown', 'row', 'backgroundColor', 'default')};
    width: 100%;
    height: ${({ theme }) => (theme.mobile ? 'unset' : 'auto !important')};
    border-bottom-width: ${({ theme }) => (theme.mobile ? theme.dropdown.row.border.width + 'px' : 0)};
    border-bottom-style: ${fromTheme('dropdown', 'row', 'border', 'style')};
    border-bottom-color: ${fromTheme('dropdown', 'row', 'border', 'color')};
`;
_ListItemGl2025 = webOnlyStyles(_ListItemGl2025)`
    cursor: pointer;

    &:hover {
        background-color: ${fromTheme('dropdown', 'row', 'backgroundColor', 'hover')};
    }
`;
const ListItemGl2025 = _ListItemGl2025;
ListItemGl2025.displayName = 'ListItem';

export const ListItem = ThemeComponentSwitcher([
    { theme: 'gl2025', component: ListItemGl2025 },
    { theme: 'old', component: ListItemOld }
]);

export const InputAndButton = styled(View).attrs({ name: 'InputAndButton' })`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    background-color: transparent;
    border-top-left-radius: ${fromTheme('borderRadius')};
    border-top-right-radius: ${fromTheme('borderRadius')};
    border-bottom-left-radius: ${props => (props.isOpen ? '0' : props.theme.borderRadius + 'px')};
    border-bottom-right-radius: ${props => (props.isOpen ? '0' : props.theme.borderRadius + 'px')};
    align-items: center;
    max-width: ${({ theme, isOpen }) =>
        theme.mobile ? (isOpen ? theme.width + 'px' : 'unset') : theme.form.entryMaxWidth + 'px'};
    padding: ${({ theme, isOpen }) => (theme.mobile && isOpen ? theme.dropdown?.mobile?.padding : 0)};
`;
InputAndButton.displayName = 'InputAndButton';

let _ComboButton = styled(Button).attrs({ name: 'ComboButton' })`
    flex-shrink: 0;
`;
export const ComboButton = nativeOnlyStyles(_ComboButton)`
    padding: 8px;
`;
ComboButton.displayName = 'ComboButton';

export const FlexLabel = styled(Label).attrs({
    name: 'flex-label',
    displayName: 'flex-label'
})`
    flex: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
`;
FlexLabel.displayName = 'FlexLabel';

export const StyledTextInput = styled(TextInput)`
    width: 100%;
`;
StyledTextInput.displayName = 'StyledTextInput';

const getComboContainerMaxHeight = ({ theme, isOpen, hasErrors }) => {
    if (!theme.mobile) {
        return fromTheme('form', 'entryMaxHeight');
    }
    if (isOpen) {
        return 'unset';
    }
    if (hasErrors) {
        const maxHeight = theme?.form?.entryMaxHeight;
        const errorOffset = theme?.form?.entryMaxHeightErrorOffset ?? 10;
        if (typeof maxHeight === 'number') {
            return `${maxHeight + errorOffset}px`;
        }
        if (typeof maxHeight === 'string' && /px$/.test(maxHeight)) {
            const parsed = Number.parseFloat(maxHeight);
            if (Number.isFinite(parsed)) {
                return `${parsed + errorOffset}px`;
            }
        }
        return `calc(${fromTheme('form', 'entryMaxHeight')} + ${errorOffset}px)`;
    }
    return fromTheme('form', 'entryMaxHeight');
};

export const _private = {
    getComboContainerMaxHeight
};

export const ComboContainer = styled(View).attrs({ name: 'combo-container' })`
    display: flex;
    flex-direction: column;
    max-width: ${({ noMaxWidth, theme }) =>
        noMaxWidth || theme.mobile ? 'unset' : fromTheme('form', 'entryMaxWidth')};
    min-width: ${fromTheme('form', 'entryMinWidth')};
    flex-basis: 1px;
    flex-grow: 1;
    min-height: ${fromTheme('form', 'entryMinHeight')};
    max-height: ${getComboContainerMaxHeight};
    position: relative;
    ${({ theme, isOpen }) =>
        theme.mobile &&
        isOpen &&
        `
            z-index: ${theme.zindex.DropDownComboContainer};
            background-color: ${theme.baseBackgroundColor};
            height: ${theme.height}px;
            width: ${theme.width}px;
            position: ${theme.native ? 'absolute' : 'fixed'};
            top: 0;
            left: 0;
        `};
`;
ComboContainer.displayName = 'ComboContainer';

function getRoundedMaxHeight(numLinesArray, maxHeight, pixelsPerLineOfText, paddingPerRow, menuPadding) {
    let heightSoFar = menuPadding;
    for (const numLinesCurrentRow of numLinesArray) {
        const potentialNewHeight = heightSoFar + (numLinesCurrentRow * pixelsPerLineOfText + paddingPerRow);
        if (potentialNewHeight > maxHeight) {
            return heightSoFar;
        } else {
            heightSoFar = potentialNewHeight;
        }
    }
    return heightSoFar;
}
